/**
 * tagGate — consent-gated loader for Google Tag Manager.
 *
 * WHY: GTM is the container that ships Meta Pixel, remarketing and non-essential
 * analytics (handoff spec §7). Loading the container is itself a request to
 * googletagmanager.com carrying IP + referrer, so a container that loads on page
 * boot has already processed personal data BEFORE any consent decision exists.
 * That failure is silent — the site looks and works identically — which is why
 * the container was removed from public/index.html and is injected here instead,
 * only after consent.isAllowed('marketing') is true.
 *
 * Consent module contract used (pinned, do not assume more):
 *   isAllowed(category) -> boolean, DEFAULT DENIED when no decision exists
 *   onConsentChange(cb) -> unsubscribe fn
 */

import { isAllowed, onConsentChange } from './consent';

// Container id is fixed for wtfgos.com. Not a secret (it ships in every page),
// but env-overridable so staging can point at a separate container.
export const GTM_CONTAINER_ID =
  process.env.REACT_APP_GTM_CONTAINER_ID || 'GTM-56MR8T9P';

const GTM_SCRIPT_ATTR = 'data-wtfgo-gtm';

/** Google Consent Mode v2 signals we control. Denied is the only safe default. */
const DENIED_ALL = Object.freeze({
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
});

function getDataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Current consent state expressed as Consent Mode v2 signals.
 * Marketing covers the three advertising signals (pixel, remarketing, ad
 * personalisation); analytics covers analytics_storage. Anything the user has
 * not affirmatively allowed stays denied.
 */
function consentSignals() {
  const marketing = isAllowed('marketing') ? 'granted' : 'denied';
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: isAllowed('analytics') ? 'granted' : 'denied',
  };
}

/**
 * Has a `consent default` already been pushed?
 *
 * WHY we scan the dataLayer instead of trusting a module-level flag: the flag is
 * lost on HMR/module re-import while the dataLayer (on window) survives, and
 * Google only honours the FIRST default command — pushing a second one after
 * tags have run is at best ignored and at worst resets state we just updated.
 */
function hasDefaultConsent() {
  return getDataLayer().some(
    (entry) =>
      Array.isArray(entry) && entry[0] === 'consent' && entry[1] === 'default'
  );
}

/**
 * Push the default-denied consent state. MUST run before GTM is injected.
 *
 * WHY it is pushed even when everything is denied: absent a default command,
 * Google assumes consent is granted and tags set advertising cookies on load.
 * The explicit denied default is what puts the container into denied mode.
 */
export function pushDefaultConsent() {
  if (hasDefaultConsent()) return false;
  getDataLayer().push(['consent', 'default', { ...DENIED_ALL }]);
  return true;
}

/**
 * Push the CURRENT consent state as a `consent update`.
 *
 * WHY this is the real withdrawal control: a container that is already loaded
 * cannot be un-injected. Only an update command with the signals flipped back to
 * denied stops an in-page GTM from continuing to set advertising cookies, and it
 * takes effect immediately — the visitor does not have to reload.
 */
export function pushConsentUpdate() {
  getDataLayer().push(['consent', 'update', consentSignals()]);
}

/**
 * Idempotency guard. WHY: onConsentChange can fire many times (banner re-render,
 * another tab writing consent, a later "manage preferences" save). Injecting the
 * container twice makes GTM initialise twice and every tag inside it fire twice
 * — which silently doubles reported conversions and halves reported CPA.
 * We check the DOM rather than only a module flag so a re-imported/HMR'd module
 * still cannot produce a second tag.
 */
function isGtmLoaded() {
  return !!document.querySelector(`script[${GTM_SCRIPT_ATTR}]`);
}

/**
 * Inject the GTM container. Safe to call repeatedly; no-ops if already loaded
 * or if NEITHER analytics nor marketing consent is granted.
 *
 * WHY EITHER CATEGORY, NOT JUST MARKETING: GTM is a CONTAINER, not a tag. GA4
 * (G-2ZKCF74FCK) is configured inside this very container — verified against the
 * live container — and the site loads no separate gtag script, so GA4 exists
 * only via GTM. Gating the container on 'marketing' alone meant a visitor who
 * granted analytics but refused marketing got no container, and therefore no
 * analytics at all, while this file happily computed analytics_storage:'granted'
 * and pushed a signal nothing was there to read.
 *
 * The container may load; what is allowed to FIRE inside it is governed by the
 * Consent Mode signals above. That separation is precisely what Consent Mode is
 * for, and it keeps advertising denied while analytics works.
 * @returns {boolean} true when a script was injected by this call.
 */
export function loadGtm() {
  if (!isAllowed('marketing') && !isAllowed('analytics')) return false;
  if (isGtmLoaded()) return false;

  // Consent Mode default MUST precede the container in the dataLayer, otherwise
  // GTM boots assuming granted and the denial arrives too late to matter.
  pushDefaultConsent();

  // Standard GTM snippet, inlined so we control ordering relative to consent.
  getDataLayer().push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    GTM_CONTAINER_ID
  )}`;
  script.setAttribute(GTM_SCRIPT_ATTR, GTM_CONTAINER_ID);
  document.head.appendChild(script);
  return true;
}

/**
 * Wire the gate up. Call once at app bootstrap.
 *
 * Loads GTM now if consent already exists (returning visitor), and subscribes so
 * that granting consent later loads it WITHOUT a page reload — otherwise a user
 * who accepts the banner is not tracked until their next navigation, and the
 * campaign that acquired them loses its conversion.
 *
 * There is deliberately no "unload": a loaded container cannot be un-injected.
 * Withdrawal is enforced by the Consent Mode v2 commands pushed here — a
 * default-denied state before the container ever loads, and an `update` on every
 * consent change carrying the CURRENT state. Withdrawing therefore pushes an
 * all-denied update that stops an already-running GTM from setting advertising
 * cookies immediately, with no page reload. Gating injection alone would leave
 * withdrawal purely cosmetic for anyone who had previously granted.
 *
 * @returns {function} unsubscribe
 */
export function initTagGate() {
  // Ordering matters: default-denied first, even if consent is denied and no
  // container is ever injected on this page view.
  pushDefaultConsent();

  // Then immediately publish the CURRENT state. Without this, a RETURNING
  // visitor who already consented spends the whole page view in denied mode:
  // the default says denied, and the update only ever fired on a subsequent
  // CHANGE — which never comes, because they decided on an earlier visit.
  // Their analytics and ads would be silently suppressed despite valid consent,
  // with no error anywhere. Only a first-time visitor who clicks the banner in
  // this same page view would have worked.
  pushConsentUpdate();

  loadGtm();
  return onConsentChange(() => {
    loadGtm();
    pushConsentUpdate();
  });
}

export default {
  initTagGate,
  loadGtm,
  pushDefaultConsent,
  pushConsentUpdate,
  GTM_CONTAINER_ID,
};
